import { ClassType } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import React, { JSX } from 'react';
import { LuCircleAlert, LuCircleCheckBig, LuInfo } from 'react-icons/lu';
import { toast } from 'react-toastify';

interface ToastLayout {
    title?: React.ReactNode;
    message?: React.ReactNode;
}

interface ToastSuccessLayout<T> extends ToastLayout {
    onSuccess?: (object: T) => void;
    onVoidSuccess?: () => void;
}

interface ToastErrorLayout extends ToastLayout {
    onError?: () => void;
}

export class ToastUtil {
    static dismiss(id: string) {
        toast.dismiss(id);
    }
    static error(title: React.ReactNode, message?: React.ReactNode, onClick?: () => void) {
        return toast.error(...this.generateToastConfig(title, message, ClassType.DANGER, undefined, onClick));
    }
    static warning(title: React.ReactNode, message?: React.ReactNode, onClick?: () => void) {
        return toast.warning(...this.generateToastConfig(title, message, ClassType.WARNING, undefined, onClick));
    }
    static success(title: React.ReactNode, message?: React.ReactNode, onClick?: () => void) {
        return toast.success(...this.generateToastConfig(title, message, ClassType.SUCCESS, undefined, onClick));
    }
    static info(title: React.ReactNode, message?: React.ReactNode, onClick?: () => void) {
        return toast.info(...this.generateToastConfig(title, message, ClassType.INFO, undefined, onClick));
    }
    static kioskError(title: React.ReactNode, message?: React.ReactNode) {
        return toast.error(...this.generateToastConfig(title, message, ClassType.DANGER, 'border! border-status-danger! text-status-danger!'));
    }
    static kioskWarning(title: React.ReactNode, message?: React.ReactNode) {
        return toast.warning(...this.generateToastConfig(title, message, ClassType.WARNING, 'border! border-status-warning! text-status-warning!'));
    }
    static kioskSuccess(title: React.ReactNode, message?: React.ReactNode) {
        return toast.success(...this.generateToastConfig(title, message, ClassType.SUCCESS, 'border! border-status-success! text-status-success!'));
    }
    static kioskInfo(title: React.ReactNode, message?: React.ReactNode) {
        return toast.info(...this.generateToastConfig(title, message, ClassType.INFO, 'border! border-status-info! text-status-info!'));
    }

    static amount(): number {
        return toast.length;
    }

    static promise<T>(promise: () => Promise<T | void>, pending: ToastLayout, success: ToastSuccessLayout<T>, error: ToastErrorLayout) {
        toast
            .promise(promise, {
                pending: {
                    render: () => {
                        return this.generateToastConfig(pending.title ? pending.title : CommonMessage.TOAST.PROMISE.PENDING, pending.message)[0];
                    }
                },
                success: {
                    render: () => {
                        return this.generateToastConfig(success.title ? success.title : CommonMessage.TOAST.PROMISE.SUCCESS, success.message)[0];
                    },
                    className: 'bg-background-surface! text-status-success!'
                },
                error: {
                    render: () => {
                        return this.generateToastConfig(error.title ? error.title : CommonMessage.TOAST.PROMISE.ERROR, error.message)[0];
                    },
                    className: 'bg-background-surface! text-status-danger!'
                }
            })
            .then((obj) => {
                if (obj) {
                    success.onSuccess && success.onSuccess(obj);
                } else {
                    success.onVoidSuccess && success.onVoidSuccess();
                }
            })
            .catch(() => error.onError && error.onError());
    }

    static show(title: React.ReactNode, message?: React.ReactNode, type?: ClassType, onClick?: () => void): void {
        this.run(this.generateToastConfig(title, message, type, undefined, onClick), type);
    }

    static generateToastConfig(
        title: React.ReactNode,
        message?: React.ReactNode,
        type?: ClassType,
        className?: string,
        onClick?: () => void
    ): [content: JSX.Element, options?: any] {
        return [
            <div>
                <div className="text-foreground-default justify-start font-['Montserrat'] text-base font-semibold">{title ? title : 'Info'}</div>
                {message && <div className="toast-body text-brand-muted justify-start font-['Montserrat'] text-base font-medium">{message}</div>}
            </div>,
            {
                onClick: onClick,
                icon: ToastUtil.getIcon(type),
                className: className ?? `bg-background-surface!`
            }
        ];
    }

    private static getIcon(type?: ClassType): JSX.Element {
        switch (type) {
            case ClassType.DANGER:
            case ClassType.WARNING:
                return <LuCircleAlert className={`text-${type} text-status-${type}`} />;
            case ClassType.SUCCESS:
                return <LuCircleCheckBig className='text-success text-status-success' />;
            case ClassType.INFO:
            case ClassType.DARK:
            case ClassType.LIGHT:
            case ClassType.PRIMARY:
            case ClassType.SECONDARY:
            default:
                return <LuInfo className={`text-${type ? type : 'primary'} text-status-${type} text-brand-${type}`} />;
        }
    }

    private static run(params: [content: JSX.Element, options?: any], type?: ClassType): void {
        switch (type) {
            case ClassType.DANGER:
                toast.error(...params);
                break;
            case ClassType.WARNING:
                toast.warning(...params);
                break;
            case ClassType.SUCCESS:
                toast.success(...params);
                break;
            case ClassType.INFO:
                toast.info(...params);
                break;
            case ClassType.DARK:
                toast.dark(...params);
                break;
            case ClassType.LIGHT:
            case ClassType.PRIMARY:
            case ClassType.SECONDARY:
            default:
                toast(...params);
        }
    }
}
